import type { UIGameState } from '@sorrem-client/types';
import type { LoadoutActionSource, LoadoutHeldAction } from '@sorrem-client/ui/components';
import { withCarriedSlotHotkey, resolveActionBarActivation } from '@sorrem-client/ui/shared/action-bar';
import { useGameplay } from '@sorrem-client/ui/features/PlayPage/gameplay-context';
import { useCallout, useGameSelector } from '@sorrem-client/ui/hooks';
import { DRAW_SKILL_ID, findEssenceAbilityForSkill, type ActionBarHotkeyTarget } from '@sorrem/shared';
import { useCallback, useMemo, useState } from 'react';
import { useEquipCarriedItem } from './use-equip-carried-item';

interface LoadoutActionPressInput {
    readonly targets: readonly ActionBarHotkeyTarget[];
    readonly setTargets: (targets: ActionBarHotkeyTarget[]) => void;
    readonly carriedItems: UIGameState['player']['carriedItems'];
    /** A carried slot waiting to be linked: the next action-bar press or click links it instead of firing. */
    readonly linkingSlotIndex: number | null;
    readonly onLinked: () => void;
}

export interface LoadoutActionPress {
    /** The action frame held down, with how far through its hold the host is; null when none. */
    heldAction: LoadoutHeldAction | null;
    onActionPress: (index: number, source: LoadoutActionSource) => void;
    onActionRelease: (index: number, source: LoadoutActionSource) => void;
}

/**
 * Pressing and letting go of the run loadout's action frames. A hotkey
 * triggers whatever occupies its linked carried slot. Pressing a crystal's
 * starts using it: the host channels the use, and holding through the
 * channel completes it, so a stray press wastes nothing. Equipment is
 * equipped (swapped in) at once. While a carried slot waits to be linked,
 * the hotkey links to it instead.
 */
export function useLoadoutActionPress({ targets, setTargets, carriedItems, linkingSlotIndex, onLinked }: LoadoutActionPressInput): LoadoutActionPress {
    const { bridge, requestConsumeCrystal, requestCancelCrystalUse, requestCastAbility, requestCancelCastAbility } = useGameplay();
    const crystalUse = useGameSelector(bridge, (state) => state.crystalUse);
    const abilityHold = useGameSelector(bridge, (state) => state.abilityHold);
    const equipCarriedItem = useEquipCarriedItem();
    // The action frame held down to use a crystal; its ring fills while the host channels the use.
    const [pressedCrystalAction, setPressedCrystalAction] = useState<{ index: number; source: LoadoutActionSource } | null>(null);
    // The action frame held down to cast an ability cast by holding; its ring fills while the host holds it.
    const [pressedAbilityAction, setPressedAbilityAction] = useState<{ index: number; source: LoadoutActionSource } | null>(null);
    const heldAction = useMemo((): LoadoutHeldAction | null => {
        if (pressedCrystalAction !== null && crystalUse !== null) {
            return { index: pressedCrystalAction.index, startedAtMs: crystalUse.startedAtMs, durationMs: crystalUse.durationMs };
        }
        if (pressedAbilityAction !== null && abilityHold !== null) {
            return { index: pressedAbilityAction.index, startedAtMs: abilityHold.startedAtMs, durationMs: abilityHold.durationMs };
        }
        return null;
    }, [abilityHold, crystalUse, pressedAbilityAction, pressedCrystalAction]);

    // The first Draw in a run retires the Draw callout for good.
    const { dismiss: markUsedDraw } = useCallout('draw');
    const onActionPress = useCallback((index: number, source: LoadoutActionSource): void => {
        if (linkingSlotIndex !== null) {
            setTargets(withCarriedSlotHotkey(targets, index, linkingSlotIndex));
            onLinked();
            return;
        }
        const activation = resolveActionBarActivation(targets[index], carriedItems);
        if (activation.kind === 'use-crystal') {
            setPressedCrystalAction({ index, source });
            requestConsumeCrystal(activation.itemId);
            return;
        }
        if (activation.kind === 'equip') {
            equipCarriedItem(activation.itemId, activation.equipmentType, activation.takesBothHands === true);
            return;
        }
        // A skill casts its Essence ability; the host refuses one with none yet. An empty slot does nothing.
        // One cast by holding (The Held Door) starts its hold, and letting go first cancels it.
        if (activation.kind === 'skill') {
            if ((findEssenceAbilityForSkill(activation.skillId)?.holdToCastMs ?? 0) > 0) {
                setPressedAbilityAction({ index, source });
            }
            requestCastAbility(activation.skillId);
            // The first Draw in a run retires the callout that names it as the key to surviving one.
            if (activation.skillId === DRAW_SKILL_ID) {
                markUsedDraw();
            }
        }
    }, [carriedItems, equipCarriedItem, linkingSlotIndex, markUsedDraw, onLinked, requestCastAbility, requestConsumeCrystal, setTargets, targets]);

    const onActionRelease = useCallback((index: number, source: LoadoutActionSource): void => {
        // Letting go early cancels; the pointer leaving a frame only cancels a pointer hold.
        // After the use completed there is nothing left to cancel, and the host ignores it.
        if (pressedCrystalAction?.index === index && pressedCrystalAction.source === source) {
            setPressedCrystalAction(null);
            requestCancelCrystalUse();
        }
        if (pressedAbilityAction?.index === index && pressedAbilityAction.source === source) {
            setPressedAbilityAction(null);
            requestCancelCastAbility();
        }
    }, [pressedAbilityAction, pressedCrystalAction, requestCancelCastAbility, requestCancelCrystalUse]);

    return { heldAction, onActionPress, onActionRelease };
}
